// Правила экрана «Отсутствия», которые можно проверить без отрисовки.
//
// ЧТО ЗДЕСЬ ЛЕЖИТ И ЧЕГО ЗДЕСЬ НЕТ. Здесь только то, что относится к ПОКАЗУ: каким значком
// отметить вид, как назвать период словами, что написать вместо пустой даты. Классификация
// («отпуск ежегодный», «декретный» и «льготный» — это один вид «отпуск») живёт в ЯДРЕ и
// приезжает готовым полем `kind`. Повторять её здесь значило бы завести второе правило,
// которое разойдётся с первым, и тогда подпись под таблицей перестанет относиться к таблице.

/** Виды, ради которых раздел и делается. Приезжают из ядра; здесь — порядок показа. */
export const ВИДЫ_ОТСУТСТВИЙ = ['отпуск', 'больничный', 'командировка', 'отгул'] as const

/**
 * Значок вида. `badge work` — то, что идёт прямо сейчас по плану; `badge lose` — прогул
 * и битые записи; `badge` без модификатора — всё остальное. Классы те же, что у сделок:
 * заводить раздельную палитру ради одного экрана значило бы, что человек учит её заново.
 */
export const КЛАСС_ВИДА: Record<string, string> = {
  'отпуск': 'badge won',
  'больничный': 'badge lose',
  'командировка': 'badge work',
  'отгул': 'badge',
  'прогул': 'badge lose',
  'работа по заявке': 'badge work',
  'прочее': 'badge',
  'без типа': 'badge',
}

export const классВида = (вид?: string | null): string =>
  (вид && КЛАСС_ВИДА[вид]) || 'badge'

/** Отметка кадров и статус командировки: согласовано — хорошо, отказ — плохо, пусто — никак. */
export function классРешения(текст?: string | null): string {
  const t = (текст ?? '').toLowerCase()
  if (!t) return 'badge'
  if (t.startsWith('соглас')) return 'badge won'
  if (t.startsWith('откл') || t.startsWith('отказ')) return 'badge lose'
  return 'badge'
}

/**
 * Что показать вместо решения, которого нет.
 *
 * Пустая клетка читается как «отказали» ровно так же часто, как «ещё не смотрели», а это
 * противоположные вещи: по первой человек не поедет, по второй — пойдёт спрашивать.
 */
export const БЕЗ_РЕШЕНИЯ = 'без отметки'

/** Идёт ли отсутствие в этот день. Границы включительно: последний день отпуска — отпуск. */
export function идётВ(день: string, с?: string | null, по?: string | null): boolean {
  if (!с || !по) return false
  return с <= день && по >= день
}

/**
 * Состояние периода относительно дня: сейчас, впереди, позади, непонятно.
 * «Непонятно» — это не «позади»: у записи может не быть дат вовсе.
 */
export type Когда = 'сейчас' | 'впереди' | 'позади' | 'без дат'

export function когда(день: string, с?: string | null, по?: string | null): Когда {
  if (!с || !по) return 'без дат'
  if (идётВ(день, с, по)) return 'сейчас'
  return с > день ? 'впереди' : 'позади'
}

/**
 * Сколько дней в периоде. Возвращает null, если дат нет или окончание раньше начала:
 * отрицательная длительность в сумме дней испортила бы её молча, а нулём её не заменишь —
 * ноль дней отпуска это тоже утверждение, и неверное.
 */
export function дней(с?: string | null, по?: string | null): number | null {
  if (!с || !по) return null
  const a = Date.parse(с + 'T00:00:00Z')
  const b = Date.parse(по + 'T00:00:00Z')
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null
  return Math.round((b - a) / 86400000) + 1
}

/** Свод «сколько строк какого вида» из ответа /refs — в порядке, удобном для чтения. */
export function поВидам(
  своды: { kind: string; rows: number | null; people: number | null }[] | undefined,
  порядок: readonly string[] = ВИДЫ_ОТСУТСТВИЙ,
): { kind: string; rows: number; people: number }[] {
  const по = new Map((своды ?? []).map((с) => [с.kind, с]))
  const начало = порядок
    .filter((в) => по.has(в))
    .map((в) => ({ kind: в, rows: по.get(в)!.rows ?? 0, people: по.get(в)!.people ?? 0 }))
  const остальное = (своды ?? [])
    .filter((с) => !входитВПорядок(порядок, с.kind))
    .map((с) => ({ kind: с.kind, rows: с.rows ?? 0, people: с.people ?? 0 }))
    .sort((a, b) => b.rows - a.rows)
  return [...начало, ...остальное]
}

const входитВПорядок = (порядок: readonly string[], вид: string) => порядок.includes(вид)
